import React, { useContext } from 'react';

import { useAppStore } from '../../../stores';
import useMapStateStore from '../../../stores/mapStateStore';

import Modal from '../../Modal/Modal';
import PlaceDetail from '../../Place/PlaceDetail';
import MapResidenceDetail from '../../Residence/MapResidenceDetail';
import FavoriteButton from '../../Personalization/Favorite/FavoriteButton';
import FavoriteButtonsContext from '../../Personalization/Favorite/FavoriteButtonsContext';
import { FAVORITE_LABEL, FAVORITED_LABEL } from '../../TourGuideAi/components/Buttons';
import { buildPlaceModalHeaderData } from '../MapPlace/MapPlaceHelpers';

const MapOverlayModals = ( {
  modalState,
  modalRootId,
  modalNavBtns,
  modalHeaderData,
  onClose,
  setModalNavBtns,
  setModalHeaderData,
} ) => {
  const personalizationActive = useAppStore( ( state ) => state.personalizationActive );
  const tgaiSessionActive = useMapStateStore( ( state ) => state.tgaiSessionActive );
  const emitFavoriteAmenity = useMapStateStore( ( state ) => state.emitFavoriteAmenity );
  // Read the context directly, as TourGuide's Buttons does: useFavoriteButtons
  // throws outside a provider, and without one the label stays unsaved.
  const isFavorited = useContext( FavoriteButtonsContext )?.isFavorited;

  // Place modals are launched from the interactive map; surface the favorite
  // button in the header when personalization is active, or during an active
  // TourGuide AI session (prospects favorite amenities mid-tour). In the tgai
  // case personalization is off, so forceVisible overrides FavoriteButton's own
  // gate. Residence modals build their own header data (with pricing) from
  // within MapResidenceDetail.
  const placeModalIsOpen = modalState.isOpen && modalState.type === 'place';
  const pin = modalState.data?.pin;
  // Only build the button when a place modal is actually open. This runs on
  // every map render, and without the gate a session-active map builds a
  // FavoriteButton with no uuid every time.
  const showFavorite = placeModalIsOpen && ( personalizationActive || tgaiSessionActive );

  // During a tgai session, report modal-header favorites back into the capture
  // pipeline (the emit resolves the uuid to the place NID). Null otherwise.
  const handleFavoriteToggle = emitFavoriteAmenity
    ? ( nowFavorited ) => emitFavoriteAmenity( pin?.uuid, pin?.label_text || pin?.modal_title, nowFavorited )
    : null;

  // Same accessible name as TourGuide's in-chat hearts (#1619), so a heart
  // reached from the map reads the same as one reached from the chat (#1628).
  const placeName = pin?.label_text || pin?.modal_title;
  const favoriteLabel = isFavorited?.( 'place', pin?.uuid ) ? FAVORITED_LABEL : FAVORITE_LABEL;

  const placeHeaderData = buildPlaceModalHeaderData(
    showFavorite && !!pin?.uuid,
    pin?.uuid,
    FavoriteButton,
    tgaiSessionActive,
    handleFavoriteToggle,
    placeName ? `${favoriteLabel}: ${placeName}` : favoriteLabel,
  );

  // TourGuide AI prospects don't recognize the bare heart, so tgai sessions get
  // a caption beside it. Not shown for personalization-only favorites.
  if ( tgaiSessionActive && placeHeaderData.components.length > 0 ) {
    placeHeaderData.components.push(
      <span
        key="favorite-hint"
        className="detail__footnote detailHeader__favoriteHint"
        data-testid="tgai-modal-favorite-hint"
      >
        Click ♡ to favorite
      </span>,
    );
  }

  return (
    <>
      {modalState.isOpen && modalState.type === 'place' && (
        <Modal
          modalClassName="modal--anchored"
          modalRootId={modalRootId}
          isOpen={modalState.isOpen}
          handleModalHide={onClose}
          showBlur
          showCloseButton
          title={modalState.data?.pin?.label_text || modalState.data?.pin?.modal_title || 'Place Details'}
          navBtns={modalNavBtns}
          headerData={placeHeaderData}
        >
          <PlaceDetail
            uuid={modalState.data?.pin?.uuid}
            parent="modal"
            setModalNavBtns={setModalNavBtns}
          />
        </Modal>
      )}

      {modalState.isOpen && modalState.type === 'unit' && (
        <Modal
          modalClassName="modal--anchored"
          modalRootId={modalRootId}
          isOpen={modalState.isOpen}
          handleModalHide={onClose}
          showBlur
          showCloseButton
          title={modalState.data?.info?.label_text}
          navBtns={modalNavBtns}
          headerData={modalHeaderData}
        >
          <MapResidenceDetail
            uuid={modalState.data?.info?.uuid}
            data={modalState.data?.info}
            setModalNavBtns={setModalNavBtns}
            setModalHeaderData={setModalHeaderData}
          />
        </Modal>
      )}
    </>
  );
};

export default MapOverlayModals;
